import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import type { Race } from './types';
import { useRaceData } from './hooks';
import { KoreaMap, RaceTooltip, RaceInfoPanel } from './components/map';
import { FilterChips } from './components/filter';
import { RaceListSidebar } from './components/sidebar';
import { LanguageToggle, Footer } from './components/layout';
import { CookieBanner } from './components/CookieBanner';
import { LoginModal, TermsModal, UserMenu, TestLoginButton } from './auth';

function App() {
  const { t } = useTranslation('common');
  const {
    races,
    filteredRaces,
    loading,
    error,
    retry,
    filter,
    setFilter,
    selectedRace,
    setSelectedRace,
    hoveredRaceId,
    setHoveredRaceId,
    regionCounts,
  } = useRaceData();

  const [hoveredRace, setHoveredRace] = useState<Race | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const showTooltip = !!hoveredRace && !selectedRace;
  useEffect(() => {
    if (!showTooltip) return;
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [showTooltip]);

  const handleRaceHover = useCallback(
    (race: Race | null) => {
      setHoveredRace(race);
      setHoveredRaceId(race?.id ?? null);
    },
    [setHoveredRaceId]
  );

  const handleClosePanel = useCallback(() => setSelectedRace(null), [setSelectedRace]);

  const handleRegionClick = useCallback(
    (regionName: string) => {
      const currentRegions = filter.region ?? [];
      const next = currentRegions.includes(regionName)
        ? currentRegions.filter((r) => r !== regionName)
        : [...currentRegions, regionName];
      setFilter({ ...filter, region: next.length > 0 ? next : undefined });
    },
    [filter, setFilter]
  );

  // Compute highlighted regions from filtered races
  const highlightedRegions = filter.region ?? [];

  // Map race locations to region names for highlighting
  const activeRegions =
    highlightedRegions.length > 0
      ? highlightedRegions
      : undefined;

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">
          <span className="header-logo-mark">
            RACE<span>/</span>MAP
          </span>
          <span className="header-logo-ko">{t('appTagline')}</span>
        </div>
        <div className="header-actions">
          <TestLoginButton />
          <LanguageToggle />
          <UserMenu />
        </div>
      </header>
      <LoginModal />
      <TermsModal />

      <main className="main" data-testid="world-map" aria-label={t('appTagline')}>
        {loading ? (
          <div className="loading">{t('loading')}</div>
        ) : error ? (
          <div className="error-state" role="alert">
            <div className="error-state-headline display">{t('error.loadFailed')}</div>
            <p className="error-state-message">
              {t('error.loadFailedMessage').split('\n').map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
            <button
              className="error-state-retry"
              onClick={retry}
            >
              {t('retry')}
            </button>
          </div>
        ) : (
          <>
            <div className="map-area">
              <FilterChips
                filter={filter}
                onFilterChange={setFilter}
                regionCounts={regionCounts}
                totalCount={races.length}
                matchedCount={filteredRaces.length}
              />
              <KoreaMap
                races={filteredRaces}
                selectedRace={selectedRace}
                onRaceSelect={setSelectedRace}
                onRaceHover={handleRaceHover}
                highlightedRegions={activeRegions}
                hoveredRaceId={hoveredRaceId}
                onRegionClick={handleRegionClick}
              />
              {hoveredRace && !selectedRace && (
                <RaceTooltip race={hoveredRace} position={mousePosition} />
              )}
              {selectedRace && (
                <RaceInfoPanel race={selectedRace} onClose={handleClosePanel} />
              )}
              <div className="map-legend">
                <h4>범례</h4>
                <div className="legend-item">
                  <span className="legend-dot status-open" />
                  <span>접수중</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot status-upcoming" />
                  <span>예정</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot status-closed" />
                  <span>마감</span>
                </div>
              </div>
            </div>
            <RaceListSidebar
              filteredRaces={filteredRaces}
              selectedRace={selectedRace}
              hoveredRaceId={hoveredRaceId}
              onRaceSelect={setSelectedRace}
              onRaceHover={handleRaceHover}
              regionTotalCounts={regionCounts}
            />
          </>
        )}
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}

export default App;
